import React from 'react';
import styled from 'styled-components';
import { MINUTES_IN_HOUR } from 'shovel-lib/utils/timeUtils';
import { isPositiveInteger } from '@utils/validators';
import {
  AutosizeFormInput,
  BareWrapper,
  INLINE_INPUT_VERTICAL_PADDING,
  INPUT_HORIZONTAL_PADDING,
  INPUT_VERTICAL_PADDING
} from '../Input.styles';
import { AlignType } from '@state/common/utils';
import { Row } from '../../layoutUtils';
import InputWrapper from '../InputWrapper';
import InlineInputWrapper from '../InlineInputWrapper';
import { RegularText } from '@utils/typography';
import Tooltip from '../../tooltip/Tooltip';
import { TooltipPosition } from '@utils/popupUtils';

const getMinutes = (duration?: number) => (duration === 0 ? 0 : Math.floor(duration! % MINUTES_IN_HOUR) || '');

const getHours = (duration?: number) => Math.floor(duration! / MINUTES_IN_HOUR) || '';

// Digits only (or nothing), and less than an hour of minutes.
const isDuration = (hours: string, minutes: string) =>
  isPositiveInteger(hours) && isPositiveInteger(minutes) && !(parseInt(minutes) >= MINUTES_IN_HOUR);

export const validate = (hours: string, minutes: string, oldValue?: number): number | undefined => {
  if (!isDuration(hours, minutes)) return oldValue;

  if (isNaN(parseInt(minutes)) && isNaN(parseInt(hours))) return undefined;

  return parseInt(hours || '0') * MINUTES_IN_HOUR + parseInt(minutes || '0');
};

export type DurationInputProps = {
  value?: number;
  name?: string;
  onChange?: (durationInMinutes?: number) => void;
  onKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  error?: string;
  touched?: boolean;
  onBlur?: (durationInMinutes?: number) => void;
  className?: string;
  label?: string;
  align?: AlignType;
  inline?: boolean;
  autosize?: boolean;
  autofocus?: boolean;
  bare?: boolean;
  inSeconds?: boolean;
  tooltip?: string;
  tooltipPosition?: TooltipPosition;
  required?: boolean;
  disabled?: boolean;
  big?: boolean;
  noErrorMargin?: boolean;
  noPadding?: boolean;
  selectOnFocus?: boolean;
};

// Each field shows the student's own text while they type, not the number rebuilt from it: rebuilt, deleting the 3 of
// "30" left 0 minutes, which shows as an empty field, so the 0 went too and typing a 2 gave "1h 2m" rather than "1h 20m"
// (Petr). The text is tidied back to the usual look when the field is left, or when the value changes from outside.
type State = { value?: number; hours: string; minutes: string };

// Whole hours show their minutes as a real "00", not the faint placeholder, which read like a field left empty: "1h 00m"
// (Petr, 2026-10-09).
const textOf = (value?: number) => {
  const hours = getHours(value).toString();
  const minutes = getMinutes(value).toString();
  return { hours, minutes: hours && (!minutes || minutes === '0') ? '00' : minutes };
};

class DurationInput extends React.PureComponent<DurationInputProps, State> {
  readonly state: State = { value: this.props.value, ...textOf(this.props.value) };
  hoursInput: null | HTMLInputElement = null;

  componentDidMount() {
    this.selectHoursIfNeeded();
    requestAnimationFrame(() => this.selectHoursIfNeeded());
  }

  componentDidUpdate(prevProps: Readonly<DurationInputProps>, prevState: Readonly<State>, snapshot?: any) {
    if (prevProps.value !== this.props.value && this.props.value !== this.state.value) {
      this.setState({ value: this.props.value, ...textOf(this.props.value) });
    }
  }

  // Mount-time focus only when the field asks for it; selectOnFocus alone still selects on a later click.
  selectHoursIfNeeded = () => {
    if (!this.props.selectOnFocus || !this.props.autofocus) return;
    const node = this.hoursInput as (HTMLElement & { input?: HTMLInputElement }) | null;
    const input = node instanceof HTMLInputElement ? node : node?.querySelector('input') || node?.input;
    if (!input) return;
    input.focus();
    input.select();
  };

  handleHoursBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    this.props.onBlur &&
      //@ts-ignore
      e.relatedTarget?.name !== 'duration-input-minutes' &&
      this.props.onBlur(validate(e.target.value, this.state.minutes, this.state.value));
    this.setState(textOf(this.state.value));
  };

  handleMinutesBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    this.props.onBlur &&
      //@ts-ignore
      e.relatedTarget?.name !== 'duration-input-hours' &&
      this.props.onBlur(validate(this.state.hours, e.target.value, this.state.value));
    this.setState(textOf(this.state.value));
  };

  handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => this.change(e.target.value, this.state.minutes);

  handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => this.change(this.state.hours, e.target.value);

  change = (hours: string, minutes: string) => {
    // A keystroke that makes no duration, like a letter or 75 minutes, is refused: the field keeps what it had.
    if (!isDuration(hours, minutes)) return;
    const value = validate(hours, minutes, this.state.value);
    this.setState({ value, hours, minutes });
    this.props.onChange?.(value);
  };

  render() {
    const {
      error,
      label,
      touched,
      className,
      inline,
      autosize,
      noErrorMargin,
      align,
      onKeyUp,
      bare = false,
      inSeconds = false,
      required = true,
      tooltip,
      autofocus,
      selectOnFocus,
      tooltipPosition,
      disabled,
      big,
      noPadding
    } = this.props;
    const { hours, minutes } = this.state;

    const Container: any = bare ? StyledBareWrapper : inline ? StyledInlineWrapper : InputWrapper;
    const smallerPadding = big ? 14 : 11;
    const biggerPadding = big ? 16 : 14;
    const children = (
      <Container
        tooltip={tooltip}
        tooltipPosition={tooltipPosition}
        label={label}
        error={error}
        align={align}
        noErrorMargin={noErrorMargin}
        className={className}
        autosize={autosize}
        touched={touched}
        required={required}
        disabled={disabled}
      >
        <Wrapper
          inline={inline}
          align={align}
          big={big}
          bare={bare}
          hasLabel={!!label}
          onClick={() => this.hoursInput && this.hoursInput.focus()}
          noPadding={noPadding}
        >
          <InputWithLabel>
            <Input
              name={'duration-input-hours'}
              padding={inSeconds ? biggerPadding : smallerPadding}
              onChange={this.handleHoursChange}
              onBlur={this.handleHoursBlur}
              placeholder={'0'}
              value={hours}
              maxLength={3}
              onKeyUp={onKeyUp}
              data-cy={'hours'}
              autoFocus={autofocus}
              onFocus={selectOnFocus ? (e: React.FocusEvent<HTMLInputElement>) => e.target.select() : undefined}
              inputRef={element => (this.hoursInput = element)}
              disabled={disabled}
              big={big ? 'true' : ''}
              minWidth={8}
              autoComplete="off"
            />
            <Label disabled={disabled}>{inSeconds ? 'm' : 'h'}</Label>
          </InputWithLabel>
          <InputWithLabel>
            <Input
              name={'duration-input-minutes'}
              padding={inSeconds ? smallerPadding : biggerPadding}
              onClick={(e: any) => e.stopPropagation()}
              onChange={this.handleMinutesChange}
              onBlur={this.handleMinutesBlur}
              placeholder={'00'}
              value={minutes}
              maxLength={2}
              onKeyUp={onKeyUp}
              data-cy={'minutes'}
              disabled={disabled}
              big={big ? 'true' : ''}
              minWidth={minutes.length === 1 ? 8 : 16}
              autoComplete="off"
            />
            <Label disabled={disabled}>{inSeconds ? 's' : 'm'}</Label>
          </InputWithLabel>
        </Wrapper>
      </Container>
    );
    if (tooltip && (bare || inline)) {
      return (
        <Tooltip
          target={inline ? <StyledBareWrapper>{children}</StyledBareWrapper> : children}
          content={tooltip}
          position={'top center'}
        />
      );
    }
    return children;
  }
}

const Input = styled(AutosizeFormInput)<{ padding: number; big?: any; minWidth: number }>`
  z-index: 1;
  input {
    ${props => (props.big ? 'font-weight: bold; font-size: 16px;' : '')};
    border: none;
    max-width: 50px;
    min-width: ${props => props.minWidth}px;
    text-align: right;
    color: ${({ theme }) => theme.textStrongColor}};
    outline: none;
    padding-right: ${props => props.padding}px;
    background-color: transparent;
  }
`;

const Label = styled.div<{ disabled?: boolean }>`
  position: absolute;
  right: 0;
  width: 1em;
  text-align: center;
  color: ${props => (props.disabled ? props.theme.captures : props.theme.textColor)};
`;

const Wrapper = styled(Row)<{
  bare?: boolean;
  inline?: boolean;
  align?: AlignType;
  big?: boolean;
  hasLabel?: boolean;
  noPadding?: boolean;
}>`
  width: 100%;
  ${props => !props.bare && `padding: 0 ${props.inline ? INLINE_INPUT_VERTICAL_PADDING : INPUT_HORIZONTAL_PADDING}px`};
  ${props => !props.bare && !props.inline && props.hasLabel && `padding-top: 14px`};
  ${Input} {
    input {
      font-weight: ${props => (props.inline ? 'bold' : 'inherit')};
      padding: ${props => (props.inline ? INLINE_INPUT_VERTICAL_PADDING : INPUT_VERTICAL_PADDING)}px 0 !important;
      ${props => props.noPadding && 'padding: 0 !important'}
    }
  }
  ${Label} {
    font-weight: ${props => (props.big ? 'bold' : props.inline ? 'normal' : 'inherit')};
    line-height: initial;
  }
  ${props => props.align === 'center' && 'justify-content: center;'}
`;

const InputWithLabel = styled(RegularText)`
  display: flex;
  flex-direction: row;
  position: relative;
  align-items: center;
`;

const StyledBareWrapper = styled(BareWrapper)`
  width: max-content;
`;

const StyledInlineWrapper = styled(InlineInputWrapper)`
  width: max-content;
`;

export default DurationInput;
